import { RecurrenceRate } from '../../utils/enums'
import { TRecurringTaskTemplate } from '../../utils/types'
import { TGoal, formatGoalAmount } from './goalTypes'

/*
 * Named `repeatsRules`, not `repeats`, because Repeats.tsx sits beside it: two modules in one
 * directory differing only in case resolve to whichever the filesystem feels like on macOS and
 * Windows, which fails the build outright while reading fine in CI. Keep the names distinct —
 * noGoalNudgeRules.ts / NoGoalNudge.tsx and recurringGoalHookUtils.ts / RecurringGoalHooks.tsx are
 * the same split.
 */

/**
 * Everything the Repeats section says about a hooked recurring template is derived here, from the
 * recurrence rate and the one stored block length. Nothing about a repeat is logged by hand, so the
 * ribbon, the row's held total, the footer and the Hold time preview must all be the same
 * derivation — two copies of this arithmetic could disagree in the same pane.
 */

/** Mon..Sun, matching luxon's `weekday` and the template's `day_to_create_task`. */
export const WEEK_DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
export const WEEK_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export type TRepeatCellState = 'held' | 'due' | 'none'
export interface TRepeatCell {
    /** 1 = Monday, 7 = Sunday. */
    weekday: number
    state: TRepeatCellState
    minutes: number
}

const isActive = (template: TRecurringTaskTemplate) => template.is_enabled && !template.is_deleted

/**
 * Which days of a week this repeat is due on. Days are read from the recurrence rate rather than
 * chosen separately: the rate is a five-value enum with no day set, so a second picker could only
 * disagree with it. Monthly and yearly repeats have no honest week read and get none.
 */
const weekdaysForRate = (rate: RecurrenceRate, weeklyDay = 1): number[] => {
    switch (rate) {
        case RecurrenceRate.DAILY:
            return [1, 2, 3, 4, 5, 6, 7]
        case RecurrenceRate.WEEK_DAILY:
            return [1, 2, 3, 4, 5]
        case RecurrenceRate.WEEKLY: {
            // A stored day outside Mon–Sun can only come from a template that used to repeat
            // monthly or yearly, where the same field holds a day of the month.
            return [weeklyDay >= 1 && weeklyDay <= 7 ? weeklyDay : 1]
        }
        default:
            return []
    }
}

export const dueWeekdays = (template: TRecurringTaskTemplate): number[] =>
    isActive(template) ? weekdaysForRate(template.recurrence_rate, template.day_to_create_task) : []

/** Whether a week ribbon can describe this repeat at all. Monthly and yearly get a text line only. */
export const hasWeekRead = (template: TRecurringTaskTemplate): boolean =>
    template.recurrence_rate === RecurrenceRate.DAILY ||
    template.recurrence_rate === RecurrenceRate.WEEK_DAILY ||
    template.recurrence_rate === RecurrenceRate.WEEKLY

export const blockMinutes = (template: TRecurringTaskTemplate): number =>
    isActive(template) ? Math.max(0, template.block_duration_minutes ?? 0) : 0

export const isHeld = (template: TRecurringTaskTemplate): boolean => blockMinutes(template) > 0

export const occurrencesPerWeekForRate = (rate: RecurrenceRate): number => weekdaysForRate(rate).length

export const occurrencesPerWeek = (template: TRecurringTaskTemplate): number =>
    isActive(template) ? occurrencesPerWeekForRate(template.recurrence_rate) : 0

export const heldMinutesPerWeek = (template: TRecurringTaskTemplate): number =>
    occurrencesPerWeek(template) * blockMinutes(template)

export const repeatWeekCells = (template: TRecurringTaskTemplate): TRepeatCell[] => {
    const due = new Set(dueWeekdays(template))
    const minutes = blockMinutes(template)
    return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
        if (!due.has(weekday)) return { weekday, state: 'none' as const, minutes: 0 }
        return { weekday, state: minutes > 0 ? ('held' as const) : ('due' as const), minutes }
    })
}

/** "45m", "2h", "2h 30m". Held time is always text as well as bar height. */
export const formatMinutes = (minutes: number): string => {
    const whole = Math.round(minutes)
    if (whole <= 0) return '0m'
    const hours = Math.floor(whole / 60)
    const rest = whole % 60
    if (hours === 0) return `${rest}m`
    if (rest === 0) return `${hours}h`
    return `${hours}h ${rest}m`
}

/** The tallest bar in a row's ribbon; a two-hour block should not draw the same as a ten-minute one. */
export const RIBBON_FULL_MINUTES = 120
export const cellFillRatio = (minutes: number): number =>
    minutes <= 0 ? 0 : Math.min(1, Math.max(0.2, minutes / RIBBON_FULL_MINUTES))

export const cellDescription = (cell: TRepeatCell): string => {
    const day = WEEK_DAY_NAMES[cell.weekday - 1]
    if (cell.state === 'held') return `${day} — ${formatMinutes(cell.minutes)} held`
    if (cell.state === 'due') return `${day} — due, no time held`
    return `${day} — nothing`
}

export interface TRepeatsSummary {
    /** The derived footer sentence; empty when there is nothing honest to say. */
    line: string
    /** A weekly target the hooked repeats can actually reach, when they cannot reach the stated one. */
    supportedTarget?: number
    /** Titles of hooked repeats holding no time. Only meaningful for an hours goal. */
    unheldTitles: string[]
}

/**
 * The footer: what the hooked repeats commit to, against the goal's weekly target.
 *
 * An hours goal is credited from the block length, so it sums held time. A sessions goal is
 * credited one unit per finished occurrence, so it counts occurrences and ignores block length
 * entirely — held time describes commitment there, never progress. A paused goal takes no penalty,
 * so it is never told it is short.
 */
export const repeatsSummary = ({
    templates,
    goal,
}: {
    templates: TRecurringTaskTemplate[]
    goal: Pick<TGoal, 'weeklyTarget' | 'unit' | 'status'>
}): TRepeatsSummary => {
    const active = templates.filter(isActive)
    const unheldTitles = active.filter((t) => !isHeld(t) && hasWeekRead(t)).map((t) => t.title)
    const target = goal.weeklyTarget ?? 0
    const isPaused = goal.status === 'paused'

    if (active.length === 0 || target <= 0) return { line: '', unheldTitles }

    if (goal.unit === 'hrs') {
        const heldHours = active.reduce((sum, t) => sum + heldMinutesPerWeek(t), 0) / 60
        if (heldHours <= 0) {
            return {
                line: `No time held yet against ${formatGoalAmount(target)} hrs a week.`,
                unheldTitles,
            }
        }
        if (heldHours >= target) {
            return {
                line: `Repeats hold ${formatGoalAmount(heldHours)} of ${formatGoalAmount(target)} hrs a week.`,
                unheldTitles,
            }
        }
        return {
            line: `Repeats hold ${formatGoalAmount(heldHours)} of ${formatGoalAmount(
                target
            )} hrs a week — ${formatMinutes((target - heldHours) * 60)} unheld.`,
            unheldTitles,
        }
    }

    const perWeek = active.reduce((sum, t) => sum + occurrencesPerWeek(t), 0)
    const line = `Repeats produce ${perWeek} of ${formatGoalAmount(target)} a week.`
    if (perWeek > 0 && perWeek < target && !isPaused) return { line, supportedTarget: perWeek, unheldTitles }
    return { line, unheldTitles }
}
